import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import useRecaptcha from '@v2/shared/lib/useRecaptcha';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { ERROR_MESSAGES, RESET_TIMEOUT } from './constants';
import type { QuoteFormData, QuoteFormPayload, SubmitError, SubmitState } from './types';

/**
 * Maps the form to what contact.php reads. The backend field names are legacy, and the
 * two fields it has no column for are folded into the comment body.
 */
function toPayload(data: QuoteFormData): QuoteFormPayload {
  return {
    name: data.name,
    EmailAddress: data.email,
    PhoneNumber: data.phone,
    cfComments: `Company: ${data.company}\n\n${data.quantity}`,
  };
}

export default function useQuoteForm() {
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [submitError, setSubmitError] = useState<SubmitError>(null);
  const { executeRecaptcha } = useRecaptcha();
  const { buildUrl } = useUrlGenerator();
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<QuoteFormData>({ mode: 'onTouched' });

  // The success state schedules a reset; unmounting before it fires would otherwise
  // set state on a gone component.
  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const onSubmit = async (data: QuoteFormData) => {
    setSubmitState('submitting');
    setSubmitError(null);

    try {
      const params = new URLSearchParams({
        ...toPayload(data),
        'g-recaptcha-response': await executeRecaptcha(),
      });

      const response = await fetch(buildUrl('/contact.php?xCmd=sendbulkorder&ajax=1'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const result = await response.json();

      if (result.error === 'N') {
        setSubmitState('success');
        resetTimer.current = setTimeout(() => {
          setSubmitState('idle');
          reset();
        }, RESET_TIMEOUT);

        return;
      }

      setSubmitError(result.error in ERROR_MESSAGES ? result.error : 'validation');
      setSubmitState('error');
    } catch (error) {
      // Surfaced rather than swallowed: a quote that never arrives is a lost sale, and
      // the visitor is told to try again either way.
      console.error('Bulk order quote submission failed', error);
      setSubmitError('ticket');
      setSubmitState('error');
    }
  };

  return {
    register,
    handleSubmit,
    control,
    errors,
    onSubmit,
    submitState,
    submitError,
    isSubmitting: submitState === 'submitting',
  };
}
